import { useState, type FC } from 'react';
import { useIntl } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import BarChartOutlined from '@mui/icons-material/BarChartOutlined';
import { BarChart } from '@mui/x-charts/BarChart';

// common
import useIsParent from 'common/hook/useIsParent';

// screen
import type {
  MenuDishStatisticsResponse,
  MenuDishItem,
  TopDishAudience,
} from 'modules/dashboard/types';

interface Props {
  data?: MenuDishStatisticsResponse | null;
}

const BLUE = '#4C6FE7';
const GREEN = '#3DBE7A';

const DishBarsCard: FC<{ title: string; items: MenuDishItem[]; color: string }> = ({
  title,
  items,
  color,
}) => {
  const { formatMessage } = useIntl();
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.disabled', py: 3, textAlign: 'center' }}>
          {formatMessage({ id: 'dashboard.noData' })}
        </Typography>
      ) : (
        (() => {
          // Nới lề trái theo tên món dài nhất để không bị cắt chữ.
          const longest = items.reduce((m, d) => Math.max(m, d.menu_display_name.length), 0);
          const labelFont = longest > 22 ? 11 : 12;
          const leftMargin = Math.min(300, Math.max(120, Math.round(longest * labelFont * 0.62) + 20));
          // Khoảng trống giữa các cột trên band scale (cột dày/mỏng).
          // Spread vào yAxis để tránh lỗi excess-property của union type (x-charts 7.29).
          const barGaps = { categoryGapRatio: 0.3, barGapRatio: 0.1 };
          return (
            <BarChart
              layout="horizontal"
              height={Math.max(140, items.length * 34)}
              yAxis={[
                {
                  scaleType: 'band',
                  data: items.map((d) => d.menu_display_name),
                  tickLabelStyle: { fontSize: labelFont },
                  ...barGaps,
                },
              ]}
              xAxis={[{ min: 0, tickMinStep: 1, valueFormatter: (v: number) => `${Math.round(v)}` }]}
              series={[{ data: items.map((d) => d.usage_count), color }]}
              grid={{ vertical: true }}
              margin={{ left: leftMargin, right: 20, top: 10, bottom: 24 }}
            />
          );
        })()
      )}
    </Paper>
  );
};

/** Nửa phải: biểu đồ top món hay dùng, tab Học sinh / Giáo viên × chế biến / không chế biến. */
const TopDishesPanel: FC<Props> = ({ data }) => {
  const { formatMessage } = useIntl();
  const [chonAudience, setChonAudience] = useState<TopDishAudience>('student');

  // Phụ huynh chỉ được xem món của HỌC SINH: ẩn hẳn tab Giáo viên và ghim đối tượng về 'student'.
  // Ghim ở đây chứ không chỉ ẩn nút, vì ẩn nút mà vẫn đọc state cũ thì chỉ cần state lỡ mang giá
  // trị 'teacher' (sau này thêm đường đặt state khác) là phụ huynh lại thấy dữ liệu giáo viên.
  const isParent = useIsParent();
  const audience: TopDishAudience = isParent ? 'student' : chonAudience;
  const danhSachAudience: TopDishAudience[] = isParent ? ['student'] : ['student', 'teacher'];

  const top = data?.top_dishes ?? null;

  const processing =
    (audience === 'student' ? top?.student_processing : top?.teacher_processing) ?? [];
  const packed = (audience === 'student' ? top?.student_packed : top?.teacher_packed) ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <BarChartOutlined fontSize="small" />
          {formatMessage({ id: 'dashboard.topDishes.sectionTitle' })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {danhSachAudience.map((a) => {
            const active = audience === a;
            return (
              <Button
                key={a}
                onClick={() => setChonAudience(a)}
                disableElevation
                variant={active ? 'contained' : 'outlined'}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  ...(active
                    ? { bgcolor: '#F26B21', '&:hover': { bgcolor: '#E05E18' } }
                    : { color: 'text.primary', borderColor: '#E2E4E8', bgcolor: '#fff' }),
                }}
              >
                {formatMessage({ id: `dashboard.topDishes.${a}` })}
              </Button>
            );
          })}
        </Box>
      </Box>
      <DishBarsCard
        title={formatMessage({ id: 'dashboard.topDishes.processing' })}
        items={processing}
        color={BLUE}
      />
      <DishBarsCard
        title={formatMessage({ id: 'dashboard.topDishes.packed' })}
        items={packed}
        color={GREEN}
      />
    </Box>
  );
};

export default TopDishesPanel;
